// =============================================================================
// Workspace Context
// =============================================================================

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { workspaceService } from '../services/workspaceService';
import { useAuth } from './AuthContext';

// Initial state
const initialState = {
  workspaces: [],
  isLoading: false,
  error: null,
  stats: {}
};

// Action types
const WORKSPACE_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_WORKSPACES: 'SET_WORKSPACES',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_WORKSPACE: 'UPDATE_WORKSPACE',
  SET_STATS: 'SET_STATS'
};

// Reducer
function workspaceReducer(state, action) {
  switch (action.type) {
    case WORKSPACE_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case WORKSPACE_ACTIONS.SET_WORKSPACES:
      return { ...state, workspaces: action.payload, isLoading: false, error: null };

    case WORKSPACE_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case WORKSPACE_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case WORKSPACE_ACTIONS.UPDATE_WORKSPACE:
      return {
        ...state,
        workspaces: state.workspaces.map(ws =>
          ws.id === action.payload.id ? { ...ws, ...action.payload } : ws
        )
      };

    case WORKSPACE_ACTIONS.SET_STATS:
      return {
        ...state,
        stats: { ...state.stats, [action.payload.workspaceId]: action.payload.stats }
      };

    default:
      return state;
  }
}

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const [state, dispatch] = useReducer(workspaceReducer, initialState);
  const { isAuthenticated } = useAuth();

  // Load workspaces
  const loadWorkspaces = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      dispatch({ type: WORKSPACE_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: WORKSPACE_ACTIONS.CLEAR_ERROR });

      const workspaces = await workspaceService.getWorkspaces();
      dispatch({ type: WORKSPACE_ACTIONS.SET_WORKSPACES, payload: workspaces });
    } catch (error) {
      console.error('Failed to load workspaces:', error);
      dispatch({ type: WORKSPACE_ACTIONS.SET_ERROR, payload: error.message });
    }
  }, [isAuthenticated]);

  // Start workspace
  const startWorkspace = useCallback(async (workspaceId) => {
    try {
      dispatch({ type: WORKSPACE_ACTIONS.CLEAR_ERROR });

      // Optimistically update status
      dispatch({
        type: WORKSPACE_ACTIONS.UPDATE_WORKSPACE,
        payload: { id: workspaceId, status: 'starting' }
      });

      const updatedWorkspace = await workspaceService.startWorkspace(workspaceId);
      dispatch({
        type: WORKSPACE_ACTIONS.UPDATE_WORKSPACE,
        payload: updatedWorkspace
      });

      return updatedWorkspace;
    } catch (error) {
      console.error('Failed to start workspace:', error);
      dispatch({ type: WORKSPACE_ACTIONS.SET_ERROR, payload: error.message });

      // Revert optimistic update
      dispatch({
        type: WORKSPACE_ACTIONS.UPDATE_WORKSPACE,
        payload: { id: workspaceId, status: 'stopped' }
      });

      throw error;
    }
  }, []);

  // Stop workspace
  const stopWorkspace = useCallback(async (workspaceId) => {
    try {
      dispatch({ type: WORKSPACE_ACTIONS.CLEAR_ERROR });

      // Optimistically update status
      dispatch({
        type: WORKSPACE_ACTIONS.UPDATE_WORKSPACE,
        payload: { id: workspaceId, status: 'stopping' }
      });

      const updatedWorkspace = await workspaceService.stopWorkspace(workspaceId);
      dispatch({
        type: WORKSPACE_ACTIONS.UPDATE_WORKSPACE,
        payload: updatedWorkspace
      });

      return updatedWorkspace;
    } catch (error) {
      console.error('Failed to stop workspace:', error);
      dispatch({ type: WORKSPACE_ACTIONS.SET_ERROR, payload: error.message });

      // Revert optimistic update
      dispatch({
        type: WORKSPACE_ACTIONS.UPDATE_WORKSPACE,
        payload: { id: workspaceId, status: 'running' }
      });

      throw error;
    }
  }, []);

  // Get workspace stats
  const getWorkspaceStats = useCallback(async (workspaceId) => {
    try {
      const stats = await workspaceService.getWorkspaceStatsById(workspaceId);
      dispatch({
        type: WORKSPACE_ACTIONS.SET_STATS,
        payload: { workspaceId, stats }
      });
      return stats;
    } catch (error) {
      console.error('Failed to get workspace stats:', error);
      throw error;
    }
  }, []);

  // Auto-load workspaces when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadWorkspaces();
    } else {
      // Clear workspaces when not authenticated
      dispatch({ type: WORKSPACE_ACTIONS.SET_WORKSPACES, payload: [] });
    }
  }, [isAuthenticated]); // 移除 loadWorkspaces 依赖

  const value = {
    workspaces: state.workspaces,
    isLoading: state.isLoading,
    error: state.error,
    stats: state.stats,
    loadWorkspaces,
    startWorkspace,
    stopWorkspace,
    getWorkspaceStats
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
